import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';

import { RootState } from 'app/store';
import { getOhsLocalStorage, setOhsLocalStorage } from 'global-services/OhsDataParse';
import { OhsGlobalSearchFilter, OhsGlobalSearchPayload } from 'search/OhsSearchModels';
import { initialGlobalSearchPayload } from 'search/store/OhsSearchSlice';
import { ModuleType } from 'global-services/constants/OhsObject';
import {
  OhsResourceCentreViewPresets,
  ResourceCentreListsRecordState,
} from 'resource-centre/models/OhsResourceCentreModels';

import {
  fetchResourceCentreListsAsync,
  fetchResourceCentreListsCountAsync,
} from './OhsResourceCentreActions';

const initialState: ResourceCentreListsRecordState = {
  isLoading: false,
  resourceCentreList: null,
  currentPage: 1,
  currentViewPreset: 'view_1',
};

export const resourceCentreSlice = createSlice({
  name: 'resourceCentre',
  initialState,
  reducers: {
    setResourceCentreCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setViewPreset: (state, action: PayloadAction<OhsResourceCentreViewPresets>) => {
      state.currentViewPreset = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchResourceCentreListsAsync.pending, (state) => {
        state.isLoading = true;
        state.resourceCentreList = {
          items: state.resourceCentreList?.items ?? [],
          pagination: state?.resourceCentreList?.pagination ?? {
            page: 0,
            totalPages: 0,
            next: '',
          },
        };
      })
      .addCase(fetchResourceCentreListsAsync.fulfilled, (state, action) => {
        if (action.payload) {
          state.resourceCentreList = action.payload;
          if (state?.resourceCentreList?.pagination?.totalPages) {
            setOhsLocalStorage(
              'resourceCentreListtotalPages',
              state.resourceCentreList.pagination.totalPages
            );
          }

          state.resourceCentreList.pagination.totalPages =
            getOhsLocalStorage('resourceCentreListtotalPages') ?? 0;
          state.isLoading = false;
        }
      })
      .addCase(fetchResourceCentreListsAsync.rejected, (state) => {
        state.resourceCentreList = null;
      })
      .addCase(fetchResourceCentreListsCountAsync.pending, (state) => {
        state.isLoading = true;
        state.resourceCentreList = {
          items: state.resourceCentreList?.items ?? [],
          pagination: state.resourceCentreList?.pagination ?? {
            totalPages: 0,
            page: 0,
            next: '',
          },
        };
      })
      .addCase(fetchResourceCentreListsCountAsync.fulfilled, (state, action) => {
        if (state.resourceCentreList && action.payload && action.payload.pagination) {
          setOhsLocalStorage(
            'resourceCentreListtotalPages',
            action?.payload?.pagination?.totalPages
          );
          state.resourceCentreList.pagination.totalPages =
            action?.payload?.pagination?.totalPages ?? 0;
        }
        state.isLoading = false;
      });
  },
});

const resourceCentreState = (state: RootState) => state.resourceCentre;

export const getOhsResourceCentreModuleState = createSelector(
  [resourceCentreState],
  (moduleState) => {
    const { isLoading, resourceCentreList, currentPage, currentViewPreset } = moduleState;
    return {
      isLoading,
      currentPage,
      currentViewPreset,
      resourceCentreList,
    };
  }
);

export const { setResourceCentreCurrentPage, setViewPreset } = resourceCentreSlice.actions;
export const resourceCentreModuleReducer = resourceCentreSlice.reducer;

export const defaultTaskSearchFilter: OhsGlobalSearchFilter = {
  ...initialGlobalSearchPayload.filter,
  modules: [ModuleType.ResourceCenter],
};

export const initialResourceCentreSearch: OhsGlobalSearchPayload = {
  searchKey: '',
  substringSearch: false,
  highlight: false,
  filter: defaultTaskSearchFilter,
};
